import {
  Day,
  Punch,
  useCreatePendingPunch,
  useDeletePunch,
  useGetUserTimeShiftQuery,
  useUpdatePendingPunchTimeMutation,
  useUpdatePunch
} from "@mc-intranet/shared";
import {
  DeleteOutlineOutlined,
  EditOutlined,
  LogoutOutlined
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack
} from "@mui/material";
import { useAtomValue } from "jotai";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  authStorageAtom,
  daysByUserQueryVariablesSelector
} from "../../../atoms";
import { useGraphQLClient } from "../../../cache";
import TimeSelector from "../TimeSelector";
import LocationModal from "./LocationModal";

interface Props {
  day: Day;
  punch: Punch;
  setCurrentDay: React.Dispatch<
    React.SetStateAction<Day | undefined | null>
  >;
}

const PunchCard: React.FC<Props> = (props) => {
  const { day, punch, setCurrentDay } = props;
  const { client } = useGraphQLClient();
  const variables = useAtomValue(daysByUserQueryVariablesSelector);

  const authStorage = useAtomValue(authStorageAtom);
  const { role, meId } = authStorage;

  const [deleting, setDeleting] = useState(false);
  const [inTime, setInTime] = useState<Moment | null>(null);
  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [open, setOpen] = useState(false);
  const approvementModalState = useState(false);
  const [, setOpenApprovementModal] = approvementModalState;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {
    deletePunchMutation,
    options: { isLoading: deletingPunch }
  } = useDeletePunch({
    day,
    setCurrentDay,
    client,
    variables
  });

  const { data: userTimeShiftData, refetch: fetchUserTimeShift } =
    useGetUserTimeShiftQuery(
      client,
      {
        userId: meId as string
      },
      {
        enabled: !!meId,
        onSuccess: () => {
          // setUserTimeShift(
          //   response?.usersPermissionsUser?.punchShiftLimitMinutes ?? 15
          // );
        }
      }
    );

  const onRemovePunch = () => {
    setDeleting(true);
    deletePunchMutation(
      {
        punchId: punch.documentId as string
      },
      {
        onError: (error: any) => {
          console.log("error ", error);
          toast.error(error?.response?.error.message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
  };

  useEffect(() => {
    if (!deletingPunch) {
      setDeleting(false);
    }
  }, [deletingPunch]);

  const { updatePunchMutation } = useUpdatePunch({
    day,
    setCurrentDay,
    client,
    variables
  });

  const onTimeChange = async (time: Moment | null, name: string) => {
    console.log("onTimeChange time :>> ", time);
    if (name === "in" && punch.in === null) {
      console.log("location");
      setInTime(time);
      setOpenLocationModal(true);
      return;
    }

    updatePunchMutation(
      {
        punchId: punch.documentId as string,
        [name]: time?.format("HH:mm:ss.SSS"),
        location: punch.location as string
      },
      {
        onError: (error: any) => {
          const _message = error?.response?.error.message;
          // const _message = error.response.errors[0].message;
          console.log("updatePunchMutation error ", _message);
          toast.warn(_message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
  };

  const onPunchOut = async () => {
    updatePunchMutation(
      {
        punchId: punch.documentId as string,
        out: (isToday ? moment() : moment("23:59:59", "HH:mm:ss")).format("HH:mm:ss.SSS"),
        location: punch.location as string
      },
      {
        onError: (error: any) => {
          const _message = error?.response?.error.message;
          // const _message = error.response.errors[0].message;
          console.log("updatePunchMutation error ", _message);
          toast.warn(_message, {
            position: toast.POSITION.TOP_CENTER
          });
        }
      }
    );
  };

  const onLocationConfermation = (location: string) => {
    if (inTime !== null) {
      updatePunchMutation(
        {
          punchId: punch.documentId as string,
          in: inTime?.format("HH:mm:ss.SSS"),
          location
        },
        {
          onError: (error: any) => {
            console.log("error ", error);
            toast.error(error?.response?.error.message, {
              position: toast.POSITION.TOP_CENTER
            });
          }
        }
      );
    } else {
      updatePunchMutation(
        {
          punchId: punch.documentId as string,
          location
        },
        {
          onError: (error: any) => {
            console.log("error ", error);
            toast.error(error?.response?.error.message, {
              position: toast.POSITION.TOP_CENTER
            });
          }
        }
      );
    }
    setOpenLocationModal(false);
    setInTime(null);
  };

  // Hook per creare pending punch (richiesta di approvazione)
  const { createPendingPunchMutation } = useCreatePendingPunch({
    day,
    setCurrentDay,
    client,
    variables
  });

  // Hook per aggiornare pending punch (quando già esiste una richiesta in attesa)
  const { mutate: updatePendingPunchTimeMutation } =
    useUpdatePendingPunchTimeMutation(client);

  // Funzione per mandare una modifica in approvazione
  const onSendOnApprovement = async (time: Moment, name: string) => {
    console.log("onSendOnApprovement - Invio richiesta di modifica:", {
      time: time.format("HH:mm:ss"),
      name
    });

    // Calcola la penalità in base alla differenza temporale
    const originalTime = punch[name as "in" | "out"];
    let penalty = 5; // Default

    if (originalTime) {
      // Parsing più robusto: prova vari formati
      const dateStr = day?.date || moment().format("YYYY-MM-DD");

      // Prova a parsare l'orario originale con vari formati
      let originalMoment = moment(
        `${dateStr} ${originalTime}`,
        ["YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
        true
      );

      // Se il parsing non è valido, prova solo l'orario
      if (!originalMoment.isValid()) {
        originalMoment = moment(
          originalTime,
          ["HH:mm:ss.SSS", "HH:mm:ss", "HH:mm"],
          true
        );
        if (originalMoment.isValid()) {
          // Imposta la data
          originalMoment.set({
            year: moment(dateStr).year(),
            month: moment(dateStr).month(),
            date: moment(dateStr).date()
          });
        }
      }

      const newMoment = moment(
        `${dateStr} ${time.format("HH:mm:ss.SSS")}`,
        "YYYY-MM-DD HH:mm:ss.SSS"
      );

      if (originalMoment.isValid() && newMoment.isValid()) {
        // Calcola la differenza in minuti (valore assoluto)
        const diffMinutes = Math.abs(newMoment.diff(originalMoment, "minutes"));

        // Imposta penalty in base alla differenza
        if (diffMinutes <= 60) {
          penalty = 5;
        } else {
          penalty = 10;
        }

        console.log("Calcolo penalty:", {
          originalTime,
          newTime: time.format("HH:mm:ss"),
          diffMinutes,
          penalty
        });
      }
    }

    // Verifica se esiste già una pendingPunch in attesa PER QUESTO CAMPO SPECIFICO
    const pendingPunches = punch.pendingPunches || [];
    const existingPending = pendingPunches.find((pp) => {
      // Deve essere in attesa (approved === null)
      if (pp?.approved !== null) return false;

      // E deve avere il campo specifico (in o out) valorizzato
      const hasField = pp?.[name as "in" | "out"];
      return hasField !== null && hasField !== undefined;
    });

    if (existingPending) {
      // Aggiorna la pendingPunch esistente
      console.log(
        "PendingPunch in attesa già esistente, aggiornamento:",
        existingPending.documentId
      );

      // Usa la mutation UPDATE_PENDING_PUNCH_TIME per aggiornare solo i campi time
      const updateData: {
        punchId: string;
        in?: string;
        out?: string;
        penalty?: number;
      } = {
        punchId: existingPending.documentId as string,
        penalty
      };

      if (name === "in") {
        updateData.in = time.format("HH:mm:ss.SSS");
      } else if (name === "out") {
        updateData.out = time.format("HH:mm:ss.SSS");
      }

      updatePendingPunchTimeMutation(updateData, {
        onSuccess: () => {
          toast.success("Richiesta di modifica aggiornata con successo!", {
            position: toast.POSITION.TOP_CENTER
          });

          // Aggiorna manualmente la cache locale
          if (setCurrentDay && day) {
            const updatedPunches = day.punches?.map((p) => {
              if (p?.documentId === punch.documentId) {
                const updatedPendingPunches = p?.pendingPunches?.map((pp) => {
                  if (pp?.documentId === existingPending.documentId) {
                    return {
                      ...pp,
                      ...(name === "in" && {
                        in: time.format("HH:mm:ss.SSS")
                      }),
                      ...(name === "out" && {
                        out: time.format("HH:mm:ss.SSS")
                      })
                    };
                  }
                  return pp;
                });

                return {
                  ...p,
                  pendingPunches: updatedPendingPunches || []
                } as Punch;
              }
              return p;
            });

            setCurrentDay({
              ...day,
              punches: (updatedPunches as Punch[]) || []
            });
          }

          setOpenApprovementModal(false);
        },
        onError: (error: any) => {
          console.log("error ", error);
          toast.error(
            error?.response?.error?.message ||
              "Errore nell'aggiornamento della richiesta",
            {
              position: toast.POSITION.TOP_CENTER
            }
          );
        }
      });
    } else {
      // Crea una nuova pendingPunch
      const pendingData: {
        dayId: string;
        punchId: string;
        in?: string;
        out?: string;
        penalty?: number;
      } = {
        dayId: day?.documentId as string,
        punchId: punch.documentId as string,
        penalty
      };

      // Salva solo il campo modificato, non entrambi
      if (name === "in") {
        pendingData.in = time.format("HH:mm:ss.SSS");
      } else if (name === "out") {
        pendingData.out = time.format("HH:mm:ss.SSS");
      }

      createPendingPunchMutation(pendingData, {
        onSuccess: () => {
          toast.success(
            "Richiesta di modifica inviata con successo! In attesa di approvazione.",
            {
              position: toast.POSITION.TOP_CENTER
            }
          );
          setOpenApprovementModal(false);
        },
        onError: (error: any) => {
          console.log("error ", error);
          toast.error(
            error?.response?.error.message ||
              "Errore nell'invio della richiesta",
            {
              position: toast.POSITION.TOP_CENTER
            }
          );
        }
      });
    }
  };

  const isAdministrator = role === "admin";
  const isToday = moment().format("YYYY-MM-DD") === day?.date;
  const canPunch = isToday || isAdministrator;

  // Determina lo stato delle pending punches
  const pendingPunches = punch.pendingPunches || [];

  console.log("pendingPunches for punch", punch.documentId, ":", pendingPunches);

  // Trova la pendingPunch più recente per un campo specifico (in o out)
  const getLatestPendingForField = (fieldName: "in" | "out") => {
    if (pendingPunches.length === 0) return null;

    // Filtra solo le pendingPunches che hanno quel campo valorizzato
    const relevantPending = pendingPunches.filter(
      (pp) =>
        pp?.[fieldName] !== null &&
        pp?.[fieldName] !== undefined
    );

    if (relevantPending.length === 0) return null;

    // Trova la più recente tra quelle filtrate
    return relevantPending.reduce((latest, current) => {
      const latestDate = moment(latest?.createdAt);
      const currentDate = moment(current?.createdAt);
      return currentDate.isAfter(latestDate) ? current : latest;
    });
  };

  // Determina lo stato da passare a ciascun TimeSelector (in o out)
  const getPendingStatusForField = (
    fieldName: "in" | "out"
  ): "pending" | "approved" | "rejected" | null => {
    const latestPending = getLatestPendingForField(fieldName);

    if (!latestPending) return null;

    const pendingStatus = latestPending?.approved;

    // Se il campo è valorizzato nella pendingPunch, mostra lo stato
    if (pendingStatus === null) return "pending";
    if (pendingStatus === true) return "approved";
    if (pendingStatus === false) return "rejected";

    return null;
  };

  const inFieldPendingStatus = getPendingStatusForField("in");
  const outFieldPendingStatus = getPendingStatusForField("out");

  // Determina l'orario della pending punch per ogni campo
  const getPendingTimeForField = (fieldName: "in" | "out"): string | null => {
    const latestPending = getLatestPendingForField(fieldName);

    if (!latestPending) return null;

    const pendingValue = latestPending?.[fieldName];

    // Restituisci il valore se esiste nella pendingPunch
    return pendingValue || null;
  };

  const inFieldPendingTime = getPendingTimeForField("in");
  const outFieldPendingTime = getPendingTimeForField("out");

  return (
    <>
      <Card
        className="time-wrapper"
        sx={{ bgcolor: "background.default", my: 2 }}
      >
        <CardContent>
          {userTimeShiftData?.usersPermissionsUser?.punchShiftLimitMinutes && (
            <Stack
              direction="row"
              // divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
              width="100%"
              alignItems="flex-start"
            >
              <Stack flex={1} direction="row" spacing={2}>
                <TimeSelector
                  label="Entrata"
                  name="in"
                  date={day?.date}
                  punchTime={punch.in}
                  disabled={day.vacation ?? false}
                  onChange={onTimeChange}
                  canEdit={isAdministrator}
                  checkIsTwoHoursFromNow={!isAdministrator}
                  userTimeShift={
                    userTimeShiftData?.usersPermissionsUser?.punchShiftLimitMinutes
                  }
                  fetchUserTimeShift={fetchUserTimeShift}
                  sendOnApprovement={onSendOnApprovement}
                  pendingStatus={inFieldPendingStatus}
                  pendingTime={inFieldPendingTime}
                />
              </Stack>
              <Stack flex={1} direction="row" spacing={2}>
                {punch.out || outFieldPendingStatus !== null ? (
                  <TimeSelector
                    label="Uscita"
                    name="out"
                    date={day.date}
                    punchTime={punch.out}
                    disabled={day.vacation || (!punch.in && !inFieldPendingStatus)}
                    onChange={onTimeChange}
                    inValue={punch.in}
                    canEdit={isAdministrator}
                    checkIsTwoHoursFromNow={!isAdministrator}
                    userTimeShift={
                      userTimeShiftData?.usersPermissionsUser?.punchShiftLimitMinutes
                    }
                    fetchUserTimeShift={fetchUserTimeShift}
                    sendOnApprovement={onSendOnApprovement}
                    pendingStatus={outFieldPendingStatus}
                    pendingTime={outFieldPendingTime}
                  />
                ) : (
                  <Box sx={{ width: "100%", height: "55px" }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={
                        (!isToday && !isAdministrator) ||
                        day?.vacation ||
                        !punch.in
                      }
                      endIcon={<LogoutOutlined />}
                      onClick={onPunchOut}
                      sx={{ height: "100%" }}
                    >
                      Uscita
                    </Button>
                  </Box>
                )}
              </Stack>

              <LoadingButton
                key="remove"
                size="large"
                startIcon={<DeleteOutlineOutlined />}
                loading={deleting}
                sx={{ minWidth: "30px", p: 0, fontSize: "1.2rem" }}
                onClick={handleClickOpen}
              />
            </Stack>
          )}
          <div className="location">
            <span>
              <b>Location</b>: {punch.location}
            </span>
            <LoadingButton
              size="large"
              startIcon={<EditOutlined />}
              onClick={() => setOpenLocationModal(true)}
            />
          </div>
        </CardContent>
      </Card>
      <Dialog open={open}>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Sicuro di voler cancellare la riga?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={handleClose}>
            No
          </Button>
          <Button variant="contained" onClick={onRemovePunch} autoFocus>
            Si
          </Button>
        </DialogActions>
      </Dialog>
      {openLocationModal && (
        <LocationModal
          visible={openLocationModal}
          location={punch.location}
          setVisibility={setOpenLocationModal}
          onLocationConfermation={onLocationConfermation}
        />
      )}
    </>
  );
};

export default PunchCard;
